import { useState, useEffect, useRef } from "react";
import { TUTORIALS } from "@/lib/tutorials";
import { useAppContext } from "@/App";
import { useTranslation } from "@/hooks/useTranslation";
import api from "@/lib/api";
import {
  BookOpen, X, Pause, Volume2, Loader2,
  ChevronDown, ChevronUp
} from "lucide-react";

export default function TutorialLedger({ role, isOpen, onClose }) {
  const tutorial = TUTORIALS[role];
  const { session } = useAppContext();
  const { t } = useTranslation(session?.lang);
  const [expandedChapter, setExpandedChapter] = useState(null);
  const [playingId, setPlayingId] = useState(null);
  const [loadingId, setLoadingId] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => {
    return () => { if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; } };
  }, []);

  useEffect(() => {
    if (!isOpen && audioRef.current) { audioRef.current.pause(); audioRef.current = null; setPlayingId(null); }
  }, [isOpen]);

  if (!tutorial || !isOpen) return null;

  const handlePlayTTS = async (chapter) => {
    if (playingId === chapter.id) {
      if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
      setPlayingId(null);
      return;
    }
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    setLoadingId(chapter.id);
    try {
      const text = t(chapter.textKey);
      const result = await api.generateTTS(text, "fable", 1.0);
      const audio = new Audio(`data:audio/mp3;base64,${result.audio}`);
      audioRef.current = audio;
      audio.onended = () => { setPlayingId(null); audioRef.current = null; };
      audio.play();
      setPlayingId(chapter.id);
    } catch (e) {
      void 0;
    }
    setLoadingId(null);
  };

  const toggleChapter = (id) => {
    setExpandedChapter(expandedChapter === id ? null : id);
  };

  return (
    <div className="fixed right-0 top-0 bottom-0 w-[340px] bg-white border-l border-slate-200 z-[200] flex flex-col animate-slideInRight shadow-2xl" data-testid="tutorial-ledger">
      <div className="p-5 border-b border-slate-200 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-amber-500 rounded-xl flex items-center justify-center text-slate-950">
            <BookOpen className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-sm font-black uppercase tracking-tight text-slate-900">{t(tutorial.titleKey)}</h2>
            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">{tutorial.chapters.length} {t("tut_chapters")}</p>
          </div>
        </div>
        <button onClick={onClose} className="p-2 bg-slate-100 rounded-xl text-slate-400 hover:text-slate-900" data-testid="close-tutorial">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-2">
        {tutorial.chapters.map((chapter, i) => {
          const isExpanded = expandedChapter === chapter.id;
          const isPlaying = playingId === chapter.id;
          const isLoading = loadingId === chapter.id;

          return (
            <div key={chapter.id} className="bg-slate-50 border border-slate-200 rounded-xl overflow-hidden" data-testid={`tutorial-chapter-${chapter.id}`}>
              <button
                onClick={() => toggleChapter(chapter.id)}
                className="w-full p-4 flex items-center justify-between text-left hover:bg-slate-100/30 transition-all"
              >
                <div className="flex items-center gap-3">
                  <span className="w-7 h-7 bg-slate-100 rounded-xl flex items-center justify-center text-[10px] font-black text-amber-400 shrink-0">
                    {i + 1}
                  </span>
                  <span className="text-xs font-bold text-slate-800 uppercase tracking-tight">{t(chapter.titleKey)}</span>
                </div>
                {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
              </button>

              {isExpanded && (
                <div className="px-4 pb-4 space-y-3 animate-fadeIn">
                  <p className="text-xs text-slate-400 leading-relaxed">{t(chapter.textKey)}</p>

                  <button
                    data-testid={`tts-play-${chapter.id}`}
                    onClick={() => handlePlayTTS(chapter)}
                    disabled={isLoading}
                    className={`w-full py-3 rounded-xl text-xs font-bold uppercase flex items-center justify-center gap-2 transition-all ${
                      isPlaying
                        ? "bg-amber-500 text-slate-950"
                        : isLoading
                        ? "bg-slate-100 text-slate-400 border border-slate-200 opacity-70"
                        : "bg-slate-100 text-slate-400 hover:bg-slate-200 border border-slate-200"
                    }`}
                  >
                    {isLoading ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> {t("tut_generating_voice")}</>
                    ) : isPlaying ? (
                      <><Pause className="w-4 h-4" /> {t("tut_stop_narration")}</>
                    ) : (
                      <><Volume2 className="w-4 h-4" /> {t("tut_listen_chapter")}</>
                    )}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="p-4 border-t border-slate-200 shrink-0">
        <p className="text-[10px] text-slate-400 font-bold text-center uppercase tracking-wider">
          {t("tut_footer_hint")}
        </p>
      </div>
    </div>
  );
}

export function TutorialToggle({ onClick, hasNew }) {
  return (
    <button
      onClick={onClick}
      data-testid="tutorial-toggle-btn"
      className={`p-2.5 rounded-xl transition-all border ${
        hasNew
          ? "bg-amber-50 border-amber-300 text-amber-400 animate-pulse"
          : "bg-slate-100 border-slate-200 text-slate-400 hover:text-slate-900"
      }`}
    >
      <BookOpen className="w-4 h-4" />
    </button>
  );
}
